import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Dropdown, Form, Offcanvas, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useFilters } from "../context/GlobalFilterContext";
import AnalysisChartProducts from "../components/analytics/AnalysisChartProducts";
import AssistantSuggestions from "../components/assistant/AssistantSuggestions";
import TypeTreeDropdown from "../components/TypeTreeDropdown";
import { createAuthFetch } from "../utils/authFetch";
import { normalizeAccount } from "../utils/normalizeAccount";

const ASSISTANT_ENDPOINT = "/amz-api/assistant/chat";
const ASSISTANT_FEEDBACK_ENDPOINT = "/amz-api/assistant/feedback";
const MESSAGES_STORAGE_KEY = "aiAssistant.messages";
const CONVERSATION_STORAGE_KEY = "aiAssistant.conversationId";
const HISTORY_LIMIT = 12;
const PRODUCT_CARD_LIMIT = 8;
const PRODUCT_CARD_MORE_LIMIT = 20;
const AD_AUDIT_STEP_ACTIONS = [
  "Find wasted ad keywords",
  "Suggest bid and budget changes",
  "Show zero-sales ad campaigns",
  "Find ad scale candidates",
];
const AD_AUDIT_STEP_INTENTS = [
  "ads_audit",
  "ad_wasted_keywords",
  "ad_bid_budget_changes",
  "ad_zero_sales_campaigns",
  "ad_scale_candidates",
];

const CHAT_COPY = {
  en: {
    starterPrompts: [
      "Which listings lost sales this month?",
      "Find products with weak profit and high ad spend.",
      "Show inventory risks.",
      "What changed in my top products recently?",
    ],
    showMoreProducts: "Show more products",
    analyzeDropCauses: "Analyze sales drop causes",
    runAdAudit: "Run ad audit for top affected SKUs",
    resultAdAudit: "Run ad audit for these SKUs",
    fetchRefundReasons: "Fetch refund/return reasons for flagged SKUs",
    resultRefundReasons: "Fetch refund/return reasons for these SKUs",
    prepareRestockPlan: "Prepare restock + ad reallocation plan",
    inventoryRisks: "Show inventory risks",
    resultInventoryRisks: "Check inventory risks for these products",
    showOosProducts: "Show products already out of stock",
    calculateReorderQty: "Calculate recommended order quantities",
    showLowStockAds: "Show products with ad spend and low stock",
    showInboundDelays: "Show inbound that may not arrive in time",
    showProfitableOosRisk: "Show profitable products at OOS risk",
    findWastedAdKeywords: "Find wasted ad keywords",
    suggestBidBudgetChanges: "Suggest bid and budget changes",
    showZeroSalesAdCampaigns: "Show zero-sales ad campaigns",
    findAdScaleCandidates: "Find ad scale candidates",
    scenarioGroup: "Scenario",
    resultGroup: "Actions for this result",
    adAuditGroup: "Ad audit",
    salesDropCausesGroup: "Sales drop causes",
    currentStepGroup: "Current step",
    resultActions: "Next actions",
    feedbackHelpful: "Helpful",
    feedbackNotHelpful: "Not helpful",
    feedbackNeedsReview: "Needs review",
    feedbackSaved: "Feedback saved",
  },
  uk: {
    inventoryRisks: "РџРѕРєР°Р¶Рё СЂРёР·РёРєРё РїРѕ Р·Р°Р»РёС€РєР°С…",
    starterPrompts: [
      "Які товари просіли в продажах цього місяця?",
      "Знайди товари зі слабким прибутком і великими витратами на рекламу.",
      "Покажи ризики по залишках.",
      "Що змінилось у топових товарах останнім часом?",
    ],
    showMoreProducts: "Показати більше товарів",
    analyzeDropCauses: "Аналізувати причини зниження",
    runAdAudit: "Аудит реклами для проблемних SKU",
    fetchRefundReasons: "Перевірити причини повернень",
    prepareRestockPlan: "План поповнення і перерозподілу реклами",
    showOosProducts: "Показати товари, які вже out of stock",
    calculateReorderQty: "Порахувати рекомендовану кількість до замовлення",
    showLowStockAds: "Показати товари з рекламою, але низьким запасом",
    showInboundDelays: "Показати inbound, який може не встигнути",
    showProfitableOosRisk: "Показати прибуткові товари під ризиком OOS",
    findWastedAdKeywords: "Знайти неефективні рекламні ключі",
    suggestBidBudgetChanges: "Запропонувати зміни ставок і бюджетів",
    showZeroSalesAdCampaigns: "Показати кампанії без продажів",
    findAdScaleCandidates: "Знайти кандидатів для масштабування реклами",
    scenarioGroup: "Сценарій",
    adAuditGroup: "Аудит реклами",
    salesDropCausesGroup: "Причини падіння продажів",
    currentStepGroup: "Поточний крок",
    feedbackHelpful: "Корисно",
    feedbackNotHelpful: "Некорисно",
    feedbackNeedsReview: "Потрібно перевірити",
    feedbackSaved: "Оцінку збережено",
  },
};

const RESULT_ACTIONS_LABELS = {
  en: "Next actions",
  uk: "Наступні дії",
};

const CLEAN_UK_COPY = {
  resultActions: "Наступні дії",
  scenarioGroup: "Сценарій",
  resultGroup: "Дії з результатом",
  resultAdAudit: "Аудит реклами для цих SKU",
  resultRefundReasons: "Перевірити повернення для цих товарів",
  resultInventoryRisks: "Перевірити ризики залишків для цих товарів",
};

const getChatCopy = (language) => {
  const copy = CHAT_COPY[language] || CHAT_COPY.en;

  return {
    ...copy,
    ...(language === "uk" ? CLEAN_UK_COPY : {}),
    resultActions:
      (language === "uk" ? CLEAN_UK_COPY.resultActions : copy.resultActions) ||
      RESULT_ACTIONS_LABELS[language] ||
      RESULT_ACTIONS_LABELS.en,
  };
};

const localizeSuggestions = (items, language) => {
  const source = Array.isArray(items) ? items : [];
  const targetCopy = getChatCopy(language);

  return source.map((suggestion) => {
    for (const currentLanguage of Object.keys(CHAT_COPY)) {
      const copy = CHAT_COPY[currentLanguage];
      const starterIndex = copy.starterPrompts.indexOf(suggestion);

      if (starterIndex >= 0) {
        return targetCopy.starterPrompts[starterIndex] || suggestion;
      }

      if (suggestion === copy.showMoreProducts) {
        return targetCopy.showMoreProducts;
      }

      if (suggestion === copy.analyzeDropCauses) {
        return targetCopy.analyzeDropCauses;
      }

      if (suggestion === copy.runAdAudit) {
        return targetCopy.runAdAudit;
      }

      if (suggestion === copy.resultAdAudit) {
        return targetCopy.resultAdAudit || suggestion;
      }

      if (suggestion === copy.fetchRefundReasons) {
        return targetCopy.fetchRefundReasons;
      }

      if (suggestion === copy.resultRefundReasons) {
        return targetCopy.resultRefundReasons || suggestion;
      }

      if (suggestion === copy.prepareRestockPlan) {
        return targetCopy.prepareRestockPlan;
      }

      if (copy.inventoryRisks && suggestion === copy.inventoryRisks) {
        return targetCopy.inventoryRisks || suggestion;
      }

      if (copy.resultInventoryRisks && suggestion === copy.resultInventoryRisks) {
        return targetCopy.resultInventoryRisks || suggestion;
      }

      if (suggestion === copy.showOosProducts) {
        return targetCopy.showOosProducts;
      }

      if (suggestion === copy.calculateReorderQty) {
        return targetCopy.calculateReorderQty;
      }

      if (suggestion === copy.showLowStockAds) {
        return targetCopy.showLowStockAds;
      }

      if (suggestion === copy.showInboundDelays) {
        return targetCopy.showInboundDelays;
      }

      if (suggestion === copy.showProfitableOosRisk) {
        return targetCopy.showProfitableOosRisk;
      }

      if (suggestion === copy.findWastedAdKeywords) {
        return targetCopy.findWastedAdKeywords;
      }

      if (suggestion === copy.suggestBidBudgetChanges) {
        return targetCopy.suggestBidBudgetChanges;
      }

      if (suggestion === copy.showZeroSalesAdCampaigns) {
        return targetCopy.showZeroSalesAdCampaigns;
      }

      if (suggestion === copy.findAdScaleCandidates) {
        return targetCopy.findAdScaleCandidates;
      }
    }

    return suggestion;
  });
};

const initialMessages = [
  {
    id: "welcome",
    role: "assistant",
    content:
      "Hi. I am an assistant for data analysis in the context of a selected Amazon account and marketplace.",
    createdAt: new Date().toISOString(),
  },
];

const readStoredMessages = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(MESSAGES_STORAGE_KEY) || "[]");
    return Array.isArray(stored) && stored.length ? stored : initialMessages;
  } catch (error) {
    return initialMessages;
  }
};

const readOptions = (key) => {
  try {
    const value = JSON.parse(localStorage.getItem(key) || "[]");
    return Array.isArray(value) ? value : [];
  } catch (error) {
    return [];
  }
};

const createMessage = (role, content, extra = {}) => ({
  id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  role,
  content,
  createdAt: new Date().toISOString(),
  ...extra,
});

const parseAssistantResponse = async (response) => {
  const text = await response.text();
  let json;

  try {
    json = text ? JSON.parse(text) : {};
  } catch (error) {
    json = {
      data: {
        reply: text,
      },
    };
  }

  const payload = json.data || json;

  return {
    reply:
      payload.reply ||
      payload.answer ||
      payload.message ||
      payload.content ||
      "The assistant returned an empty response.",
    conversationId:
      payload.conversationId ||
      payload.threadId ||
      payload.sessionId ||
      json.conversationId ||
      null,
    suggestions:
      payload.suggestions ||
      payload.quickReplies ||
      payload.followUps ||
      [],
    meta: {
      intent: payload.intent || null,
      insightId: payload.insightId || null,
      dataScope: payload.dataScope || payload.preparedData || payload.backendData || null,
      sources: payload.sources || payload.datasets || [],
    },
  };
};

const formatTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const normalizeImageUrl = (value) => {
  if (!value) return null;

  const url = String(value).trim();
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("//")) return `https:${url}`;
  if (url.startsWith("/")) return `https://app.profitpalms.com${url}`;

  return url;
};

const findProductsInScope = (dataScope) => {
  if (!dataScope || typeof dataScope !== "object") {
    return { rows: [], total: 0, label: "Products" };
  }

  const scopes = [
    dataScope,
    dataScope.preparedData,
    dataScope.backendData,
    dataScope.data,
    dataScope.preparedDataSummary,
  ].filter(Boolean);

  const collections = [
    { key: "topDrops", sampleKey: "topDropsSample", label: "Sales drops" },
    { key: "weakProfitProducts", sampleKey: "weakProfitProductsSample", label: "Weak profit + ad spend" },
    { key: "oosProducts", sampleKey: "oosProductsSample", label: "Out of stock products" },
    { key: "inventoryOosRows", sampleKey: "inventoryOosRowsSample", label: "Out of stock products" },
    { key: "reorderRows", sampleKey: "reorderRowsSample", label: "Recommended order quantities" },
    { key: "inventoryReorderRows", sampleKey: "inventoryReorderRowsSample", label: "Recommended order quantities" },
    { key: "lowStockAdRows", sampleKey: "lowStockAdRowsSample", label: "Low stock with ad spend" },
    { key: "inventoryLowStockAdRows", sampleKey: "inventoryLowStockAdRowsSample", label: "Low stock with ad spend" },
    { key: "inboundDelayRows", sampleKey: "inboundDelayRowsSample", label: "Inbound delay risks" },
    { key: "inventoryInboundDelayRows", sampleKey: "inventoryInboundDelayRowsSample", label: "Inbound delay risks" },
    { key: "profitableOosRiskRows", sampleKey: "profitableOosRiskRowsSample", label: "Profitable OOS risks" },
    { key: "inventoryProfitableOosRiskRows", sampleKey: "inventoryProfitableOosRiskRowsSample", label: "Profitable OOS risks" },
    { key: "stockRisks", sampleKey: "stockRisksSample", label: "Inventory risks" },
    { key: "inventoryRiskRows", sampleKey: "inventoryRiskRowsSample", label: "Inventory risks" },
    { key: "topProductChanges", sampleKey: "topProductChangesSample", label: "Top product changes" },
    { key: "topProducts", sampleKey: "topProductsSample", label: "Top products" },
    { key: "products", sampleKey: "productsSample", label: "Products" },
  ];

  for (const scope of scopes) {
    for (const collection of collections) {
      const rows = asArray(scope[collection.key]);
      const sampleRows = asArray(scope[collection.sampleKey]);

      if (rows.length || sampleRows.length) {
        return {
          rows: rows.length ? rows : sampleRows,
          total:
            rows.length ||
            Number(scope[`${collection.key}Count`]) ||
            sampleRows.length,
          label: collection.label,
        };
      }
    }
  }

  return { rows: [], total: 0, label: "Products" };
};

const getProductRowsFromScope = (dataScope) => findProductsInScope(dataScope).rows;

const extractProductFilters = (rows, limit = 50) =>
  rows
    .filter((item) => item && (item.asin || item.sku))
    .slice(0, limit)
    .map((item) => ({
      idaccount: item.idaccount ?? null,
      asin: item.asin ? String(item.asin) : null,
      sku: item.sku ? String(item.sku) : null,
    }));

const detectTextLanguage = (value) => {
  const text = String(value || "").trim();
  if (!text) return "en";

  return /[А-Яа-яІіЇїЄєҐґ]/u.test(text) ? "uk" : "en";
};

const detectChatLanguage = (items) => {
  const firstUserMessage = items.find((message) => message?.role === "user");
  return firstUserMessage ? detectTextLanguage(firstUserMessage.content) : "en";
};

const isShowMoreProductsRequest = (value) =>
  /(show\s+more|more\s+products|показати\s+більше|показать\s+больше|ще\s+товар|еще\s+товар|більше\s+товар|больше\s+товар)/iu.test(
    String(value || "")
  );

const isAnalyzeCausesRequest = (value) =>
  /(аналізувати\s+причин|аналіз\s+причин|анализ\s+причин|причин.*знижен|причин.*снижен|чому.*зниз|почему.*сниз|why.*drop|cause.*drop|drop.*cause|reason.*drop|analy[sz]e.*drop|sales\s+drop\s+causes)/iu.test(
    String(value || "")
  );

const isAdAuditRequest = (value) =>
  isCopyActionRequest(value, "runAdAudit") ||
  isCopyActionRequest(value, "resultAdAudit") ||
  /(run\s+ad\s+audit|ad\s+audit|ads\s+audit|ppc\s+audit|campaign\s+audit|keyword\s+audit|bid\s+audit|wasted\s+ad\s+keywords|bid\s+and\s+budget|zero-sales\s+ad\s+campaigns|ad\s+scale\s+candidates|аудит.*реклам|реклам.*аудит|аналіз.*реклам|анализ.*реклам|неефективн.*реклам.*ключ|зміни\s+ставок|ставок\s+і\s+бюджет|кампанії\s+без\s+продаж|масштабування\s+реклам)/iu.test(
    String(value || "")
  );

const isCopyActionRequest = (value, key) =>
  Object.keys(CHAT_COPY).some((language) => String(value || "") === getChatCopy(language)[key]);

const isUnsupportedAuditAction = (value) =>
  /(pause|paused|stop|push|raise|scale|export|csv|bulk|savings|bid\s+to\s+raise|ставк|експорт|зупин|пауза)/iu.test(
    String(value || "")
  );

const isWastedAdKeywordsRequest = (value) =>
  /(wasted\s+ad\s+keywords|wasted\s+keywords|ineffective\s+ad\s+keywords|non[-\s]?performing\s+keywords|spend\s+without\s+sales|keywords?\s+to\s+pause|неефективн.*реклам.*ключ|реклам.*ключ.*без.*продаж|ключ.*без.*продаж)/iu.test(
    String(value || "")
  );

const isBidBudgetChangesRequest = (value) =>
  /(bid\s+and\s+budget|budget\s+and\s+bid|bid\s+changes?|budget\s+changes?|change\s+bids?|adjust\s+bids?|adjust\s+budgets?|bid\s+recommendations?|budget\s+recommendations?|зміни\s+ставок|ставок\s+і\s+бюджет|ставк.*бюджет|бюджет.*ставк|рекомендац.*ставк|рекомендац.*бюджет)/iu.test(
    String(value || "")
  );

const isZeroSalesAdCampaignsRequest = (value) =>
  isCopyActionRequest(value, "showZeroSalesAdCampaigns") ||
  /(zero[-\s]?sales\s+ad\s+campaigns?|campaigns?\s+without\s+sales|ads?\s+without\s+sales|spend\s+without\s+sales|no[-\s]?sales\s+campaigns?)/iu.test(
    String(value || "")
  );

const isAdScaleCandidatesRequest = (value) =>
  isCopyActionRequest(value, "findAdScaleCandidates") ||
  /(ad\s+scale\s+candidates?|scale\s+ad|scaling\s+candidates?|scale\s+candidates?|increase\s+budget|increase\s+bids?|campaigns?\s+to\s+scale|candidates?\s+for\s+scaling)/iu.test(
    String(value || "")
  );

const isRefundReasonsRequest = (value) =>
  isCopyActionRequest(value, "fetchRefundReasons") ||
  isCopyActionRequest(value, "resultRefundReasons") ||
  /(fetch\s+refund|refund\/return|refund\s+reason|refund\s+reasons|return\s+reason|return\s+reasons|returns?\s+reason|причин.*повернен|повернен.*причин|возврат.*причин|причин.*возврат)/iu.test(
    String(value || "")
  );

const isRestockPlanRequest = (value) =>
  /(prepare\s+restock|restock.*ad|restock\s+plan|inventory\s+plan|ad\s+reallocation|reallocation\s+plan|план.*поповнен|поповнен.*план|перерозподіл.*реклам|перераспредел.*реклам|остатк.*план|залишк.*план)/iu.test(
    String(value || "")
  );

const isInventoryRisksRequest = (value) =>
  isCopyActionRequest(value, "inventoryRisks") ||
  isCopyActionRequest(value, "resultInventoryRisks") ||
  /(inventory\s+risks?|stock\s+risks?|low\s+stock|oos\b|out\s+of\s+stock|stockout|stock\s*out|no\s+stock|ризик.*(запас|залишк|склад)|(запас|залишк|склад).*ризик|низьк.*(запас|залишк|склад)|закінч.*(запас|залишк|склад))/iu.test(
    String(value || "")
  );

const isOosProductsRequest = (value) =>
  isCopyActionRequest(value, "showOosProducts") ||
  /(already\s+out\s+of\s+stock|products?\s+out\s+of\s+stock|out\s+of\s+stock\s+products?|oos\s+products?|stockout\s+products?|вже.*(out\s+of\s+stock|oos|випал|аут)|випал.*(out\s+of\s+stock|oos|аут)|товар.*(out\s+of\s+stock|oos|аут)|немає.*викуп|нема.*наяв)/iu.test(
    String(value || "")
  );

const isReorderQtyRequest = (value) =>
  isCopyActionRequest(value, "calculateReorderQty") ||
  /(recommended\s+order\s+quantit|recommended\s+order\s+qty|reorder\s+quantit|reorder\s+qty|order\s+quantit|how\s+many\s+.*order|calculate\s+.*order|purchase\s+qty|кільк.*замовити|скільки.*замовити|кільк.*до\s+замовл|рекоменд.*кільк.*замов|порах.*замов)/iu.test(
    String(value || "")
  );

const isLowStockAdsRequest = (value) =>
  isCopyActionRequest(value, "showLowStockAds") ||
  /(ad\s+spend.*low\s+(stock|inventory)|low\s+(stock|inventory).*ad\s+spend|advertis.*low\s+(stock|inventory)|low\s+(stock|inventory).*advertis|ads?.*inventory\s+risk|ppc.*low\s+(stock|inventory)|реклам.*(низьк|мал).*(запас|залишк|склад)|(низьк|мал).*(запас|залишк|склад).*реклам)/iu.test(
    String(value || "")
  );

const isInboundDelayRequest = (value) =>
  isCopyActionRequest(value, "showInboundDelays") ||
  /(inbound.*(late|delay|delayed|risk|time|arrive|arrival)|in\s+transit.*(late|delay|delayed|risk|time|arrive|arrival)|goods?\s+in\s+transit|shipment.*(late|delay|delayed|risk|arrive|arrival)|інбаунд.*(не\s+встиг|затрим|ризик)|inbound.*(не\s+встиг|затрим|ризик)|(товар|шипмент).*дороз.*(не\s+встиг|затрим|ризик)|дороз.*(не\s+встиг|затрим|ризик))/iu.test(
    String(value || "")
  );

const isProfitableOosRiskRequest = (value) =>
  isCopyActionRequest(value, "showProfitableOosRisk") ||
  /(profitable|profit).*(oos|out\s+of\s+stock|stockout|stock\s+risk)|(oos|out\s+of\s+stock|stockout|stock\s+risk).*(profitable|profit)|(прибутк|прибыльн).*(oos|out\s+of\s+stock|аут|ризик.*(запас|залишк|склад)|закінч.*(запас|залишк|склад))|(ризик.*(oos|запас|залишк|склад)|закінч.*(запас|залишк|склад)).*(прибутк|прибыльн)/iu.test(
    String(value || "")
  );

const isTopProductsChangesRequest = (value) =>
  Object.values(CHAT_COPY).some((copy) => String(value || "") === copy.starterPrompts?.[3]) ||
  /(what\s+changed|changes?|changed|recently|recent).*(top\s+products?|top\s+listings?|best\s+sellers?)|(top\s+products?|top\s+listings?|best\s+sellers?).*(what\s+changed|changes?|changed|recently|recent)|(що|шо).*(змін|змiн).*(топ|топов|товар|лістинг|листинг)|(топ|топов).*(товар|лістинг|листинг).*(змін|змiн|останн|нещодав)/iu.test(
    String(value || "")
  );
const isShowMoreResultMessage = (message) =>
  message?.role === "assistant" &&
  /(showing\s+more\s+products|no\s+additional\s+products)/iu.test(
    String(message.content || "")
  );

const findLastProductMessage = (items, options = {}) => {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const message = items[index];
    const rows = getProductRowsFromScope(message?.meta?.dataScope);

    if (options.excludeShowMoreResults && isShowMoreResultMessage(message)) {
      continue;
    }

    if (message?.role === "assistant" && rows.length) {
      return message;
    }
  }

  return null;
};

const findLastAssistantMessage = (items) => {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const message = items[index];
    if (message?.role === "assistant") {
      return message;
    }
  }

  return null;
};

const findLastScopeMessageByIntent = (items, intent) => {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const message = items[index];
    if (message?.role === "assistant" && message?.meta?.dataScope?.intent === intent) {
      return message;
    }
  }

  return null;
};

const findLastScenarioRootMessage = (items) => {
  const scenarioRootIntents = [
    "sales_drop_analysis",
    "weak_profit_ad_spend",
    "inventory_risks",
    "top_products_changes",
  ];

  for (let index = items.length - 1; index >= 0; index -= 1) {
    const message = items[index];
    const intent = message?.meta?.dataScope?.intent;
    const isResultActionScope = !!message?.meta?.dataScope?.resultAction;
    const rows = getProductRowsFromScope(message?.meta?.dataScope);

    if (
      message?.role === "assistant" &&
      !isResultActionScope &&
      scenarioRootIntents.includes(intent) &&
      rows.length
    ) {
      return message;
    }
  }

  return null;
};

const findLastResultActionSourceMessage = (items) =>
  findLastScenarioRootMessage(items) ||
  findLastProductMessage(items);

const findLastShowMoreSourceMessage = (items) =>
  findLastProductMessage(items) ||
  findLastScenarioRootMessage(items);

const hasScopeIntent = (items, intent) =>
  items.some((message) => message?.role === "assistant" && message?.meta?.dataScope?.intent === intent);

const extractScopeDateRange = (dataScope) => {
  const range = dataScope?.dateRange;

  if (!range || typeof range !== "object") return null;
  if (range.start && range.end) {
    return {
      start: range.start,
      end: range.end,
    };
  }
  if (range.current?.start && range.current?.end) {
    return {
      start: range.current.start,
      end: range.current.end,
    };
  }

  return null;
};

const formatMetric = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: Number.isInteger(number) ? 0 : 2,
  }).format(number);
};

const metricNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const formatPlainMetric = (value, options = {}) => {
  const number = metricNumber(value);
  if (number === null) return "-";

  const formatted = new Intl.NumberFormat("en-US", {
    maximumFractionDigits:
      options.maximumFractionDigits ?? (Number.isInteger(number) ? 0 : 2),
  }).format(number);

  return `${options.prefix || ""}${formatted}${options.suffix || ""}`;
};

const formatSignedMetric = (value, options = {}) => {
  const number = metricNumber(value);
  if (number === null) return "-";
  const sign = number > 0 ? "+" : number < 0 ? "-" : "";

  return `${sign}${formatPlainMetric(Math.abs(number), options)}`;
};

const pctChangeScore = (before, after) => {
  const previous = metricNumber(before);
  const current = metricNumber(after);
  if (previous === null || current === null) return 0;
  if (previous === 0) return current === 0 ? 0 : 100;

  return Math.min(Math.abs(((current - previous) / Math.abs(previous)) * 100), 250);
};

const buildBeforeAfterMetric = ({ label, before, after, delta, score, options }) => {
  const previous = metricNumber(before);
  const current = metricNumber(after);
  if (previous === null || current === null) return null;

  const computedDelta =
    metricNumber(delta) !== null ? metricNumber(delta) : current - previous;

  return {
    label,
    value: `${formatPlainMetric(previous, options)} → ${formatPlainMetric(
      current,
      options
    )} (${formatSignedMetric(computedDelta, options)})`,
    hasChange: Math.abs(computedDelta) > 0.0001,
    score:
      score ??
      Math.max(
        pctChangeScore(previous, current),
        Math.min(Math.abs(computedDelta), 100)
      ),
  };
};

const buildTopProductChangeMetrics = (product, isUk, options = {}) => {
  const labels = {
    sales: isUk ? "Продажі" : "Sales",
    revenue: isUk ? "Виручка" : "Revenue",
    profit: isUk ? "Прибуток" : "Profit",
    returns: isUk ? "Поверн." : "Returns",
    rating: isUk ? "Рейтинг" : "Rating",
    price: isUk ? "Ціна" : "Price",
    fee: isUk ? "Комісія" : "Fee",
    stock: isUk ? "Запас" : "Stock",
    daysLeft: isUk ? "дн." : "d left",
  };

  const salesMetric = buildBeforeAfterMetric({
    label: labels.sales,
    before: product.sales_previous_units,
    after: product.sales_current_units,
    delta: product.sales_delta_units,
    score: pctChangeScore(product.sales_previous_units, product.sales_current_units) + 10,
  });

  const allCandidates = [
    buildBeforeAfterMetric({
      label: labels.revenue,
      before: product.amount_previous,
      after: product.amount_current,
      delta: product.amount_delta,
      score: pctChangeScore(product.amount_previous, product.amount_current),
      options: { prefix: "$" },
    }),
    buildBeforeAfterMetric({
      label: labels.profit,
      before: product.estimated_profit_previous,
      after: product.estimated_profit_current,
      delta: product.profit_delta,
      score: pctChangeScore(product.estimated_profit_previous, product.estimated_profit_current),
      options: { prefix: "$" },
    }),
    buildBeforeAfterMetric({
      label: "ACOS",
      before: product.acos_previous,
      after: product.acos_current,
      delta: product.acos_delta,
      score: Math.min(Math.abs(metricNumber(product.acos_delta) || 0), 150),
      options: { suffix: "%" },
    }),
    buildBeforeAfterMetric({
      label: labels.returns,
      before: product.refund_qty_previous,
      after: product.refund_qty_current,
      delta: product.refund_delta,
      score: Math.min(Math.abs(metricNumber(product.refund_delta) || 0) * 12, 120),
    }),
    buildBeforeAfterMetric({
      label: "BSR",
      before: product.bsr_previous,
      after: product.bsr_current,
      delta:
        metricNumber(product.bsr_current) !== null &&
        metricNumber(product.bsr_previous) !== null
          ? metricNumber(product.bsr_current) - metricNumber(product.bsr_previous)
          : null,
      score: pctChangeScore(product.bsr_previous, product.bsr_current),
    }),
    buildBeforeAfterMetric({
      label: labels.rating,
      before: product.rating_previous,
      after: product.rating_current,
      score: Math.min(Math.abs((metricNumber(product.rating_current) || 0) - (metricNumber(product.rating_previous) || 0)) * 80, 120),
      options: { maximumFractionDigits: 1 },
    }),
    buildBeforeAfterMetric({
      label: labels.price,
      before: product.price_previous,
      after: product.price_current,
      score: pctChangeScore(product.price_previous, product.price_current),
      options: { prefix: "$" },
    }),
    buildBeforeAfterMetric({
      label: labels.fee,
      before: product.fee_previous,
      after: product.fee_current,
      score: pctChangeScore(product.fee_previous, product.fee_current),
      options: { prefix: "$" },
    }),
  ].filter(Boolean);

  const candidates = allCandidates
    .filter((metric) => metric.score >= 8)
    .sort((a, b) => b.score - a.score);

  const sourceMetrics = options.includeAll
    ? [salesMetric, ...allCandidates]
    : [salesMetric, ...candidates];

  const metrics = sourceMetrics
    .filter(Boolean)
    .filter((metric) => metric.hasChange)
    .filter((metric, index, all) => all.findIndex((item) => item.label === metric.label) === index)
    .sort((a, b) => {
      if (a.label === labels.sales) return -1;
      if (b.label === labels.sales) return 1;
      return b.score - a.score;
    })
    .slice(0, options.includeAll ? 20 : 4);

  return metrics.map(({ label, value }) => ({ label, value }));
};

const isUnsupportedActionTailHeading = (line) => {
  const normalized = String(line || "")
    .trim()
    .replace(/^#+\s*/, "")
    .replace(/^[-*•]\s*/, "")
    .replace(/^\d+[.)]\s*/, "")
    .toLowerCase();

  return /^(якщо\s+(потрібно|треба|хочете|бажаєте)|за\s+потреби)[\s,.:;!?—–-]*(я\s+)?(можу|підготую|згенерую|експортую)|^(if\s+(needed|required|you\s+want|you'd\s+like|useful)|would\s+you\s+like\s+me\s+to|i\s+can\s+(also\s+)?(prepare|generate|export|run|pull|create))/iu.test(
    normalized
  );
};

const sanitizeAssistantReplyText = (value) => {
  const lines = String(value || "").split("\n");
  const tailIndex = lines.findIndex((line, index) => index > 0 && isUnsupportedActionTailHeading(line));

  return (tailIndex >= 0 ? lines.slice(0, tailIndex) : lines).join("\n").trim();
};

const stripMarkdownImages = (value) =>
  sanitizeAssistantReplyText(value)
    .replace(/\n\s*1\s*\n\s*0[.)]\s+/g, "\n10) ")
    .replace(/(\d{4}-\d{2}-)[ \t]*\n+[ \t]*(\d{2})/g, "$1$2")
    .replace(/(\$\d+(?:[.,]\d{1,2})?)(?=[A-ZА-ЯІЇЄҐ])/gu, "$1\n")
    .replace(/([^\n\d.$])(\d{1,2}[.)][ \t]*\*\*)/g, "$1\n$2")
    .replace(/([^\n\d.$])(\d{1,2}[.)][ \t]+[A-ZА-ЯІЇЄҐ])/gu, "$1\n$2")
    .replace(/([:.;!?)]\s*)([1-9])(?=[A-ZА-ЯІЇЄҐ])/gu, "$1\n$2 ")
    .split("\n")
    .map((line) => line.replace(/\s*[-*]?\s*!\[[^\]]*]\([^)]+\)\s*/g, "").trimEnd())
    .filter((line) => line.trim() !== "")
    .join("\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();

const renderMessageLine = (line, key) => {
  const match = line.match(/^\s*([1-9][.)]?(?!\d)|1\d[.)]|20[.)])\s*(.*)$/);

  if (!match) {
    return <span key={key}>{line}</span>;
  }

  return (
    <span className="ai-message-list-line" key={key}>
      <span className="ai-message-list-number">{match[1].replace(/[.)]$/, "")}</span>
      <span>{match[2]}</span>
    </span>
  );
};

const normalizeMessageLines = (lines) => {
  const result = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const datePrefixMatch = line.match(/(\d{4}-\d{2}-)\s*$/);
    const ratioPrefixMatch = line.match(/(\d+\/)\s*$/);

    if (ratioPrefixMatch) {
      let nextIndex = index + 1;
      while (nextIndex < lines.length && lines[nextIndex].trim() === "") {
        nextIndex += 1;
      }

      const ratioRestMatch =
        nextIndex < lines.length ? lines[nextIndex].match(/^(\d{1,3})(?:[.)])?\s*(.*)$/) : null;
      if (ratioRestMatch) {
        result.push(`${line.replace(/\s*$/, "")}${ratioRestMatch[1]}`);
        if (ratioRestMatch[2]) {
          result.push(ratioRestMatch[2]);
        }
        index = nextIndex;
        continue;
      }
    }

    if (datePrefixMatch) {
      let nextIndex = index + 1;
      while (nextIndex < lines.length && lines[nextIndex].trim() === "") {
        nextIndex += 1;
      }

      if (nextIndex < lines.length && /^\d{2}\s*$/.test(lines[nextIndex])) {
        result.push(`${line.replace(/\s*$/, "")}${lines[nextIndex].trim()}`);
        index = nextIndex;
        continue;
      }

      const dateAndTextMatch =
        nextIndex < lines.length ? lines[nextIndex].match(/^(\d{2})[.)]\s*(.+)$/) : null;
      if (dateAndTextMatch) {
        result.push(`${line.replace(/\s*$/, "")}${dateAndTextMatch[1]}`);
        result.push(dateAndTextMatch[2]);
        index = nextIndex;
        continue;
      }
    }

    result.push(line);
  }

  return result;
};

const isRecommendationsHeading = (line) =>
  /^(короткі\s+рекомендації|рекомендації|краткие\s+рекомендации|рекомендации|short\s+recommendations|quick\s+recommendations|recommendations|recommended\s+checks|required\s+checks|next\s+checks|limits\s*&\s*notes|notes\s*&\s*limits|примітки\s+та\s+обмеження|обмеження\s+та\s+примітки)(?:\s|:|\(|$)/i.test(
    String(line || "")
      .trim()
      .replace(/^#+\s*/, "")
      .replace(/^[-•*]\s*/, "")
  );

const normalizeNextAnalysisLabel = (value) => {
  const label = String(value || "").replace(/\s+/g, " ").trim().replace(/[.?!]+$/, "");
  const lower = label.toLowerCase();

  if (/restock|stock|inventory|reallocation/.test(lower)) {
    return CHAT_COPY.en.prepareRestockPlan;
  }

  if (/ad|ppc|campaign|keyword|bid/.test(lower)) {
    return CHAT_COPY.en.runAdAudit;
  }

  if (/refund|return/.test(lower)) {
    return CHAT_COPY.en.fetchRefundReasons;
  }

  return null;
};

const rowHasCause = (row, cause) =>
  Array.isArray(row?.possible_causes) && row.possible_causes.includes(cause);

const buildCauseActionSuggestions = (dataScope) => {
  const rows = getProductRowsFromScope(dataScope);
  if (!rows.length) return [];

  const hasAdsSignal = rows.some(
    (row) =>
      rowHasCause(row, "ads_drop") ||
      Number(row?.current_ad_spend) > 0 ||
      Number(row?.previous_ad_spend) > 0 ||
      Number(row?.current_ad_sales) > 0 ||
      Number(row?.previous_ad_sales) > 0
  );
  const hasRefundSignal = rows.some(
    (row) =>
      rowHasCause(row, "refunds_increase") ||
      Number(row?.current_refund_quantity) > Number(row?.previous_refund_quantity || 0)
  );
  const hasStockSignal = rows.some(
    (row) =>
      rowHasCause(row, "out_of_stock_or_oos_days") ||
      rowHasCause(row, "low_stock") ||
      Number(row?.oos_days) > 0 ||
      Number(row?.latest_stock) <= 5
  );

  return [
    hasAdsSignal ? CHAT_COPY.en.runAdAudit : null,
    hasRefundSignal ? CHAT_COPY.en.fetchRefundReasons : null,
    hasStockSignal ? CHAT_COPY.en.prepareRestockPlan : null,
  ].filter(Boolean);
};

const extractNextAnalysisSuggestions = (content) => {
  const text = String(content || "");
  const suggestions = [];
  const questionMatch = text.match(/would\s+you\s+like\s+me\s+to\s+(.+?)(?:\?|$)/is);
  if (questionMatch) {
    const optionText = questionMatch[1].replace(/\s+/g, " ");
    const optionMatches = [...optionText.matchAll(/\(([A-C])\)\s*/gi)];

    optionMatches.forEach((optionMatch, index) => {
      const start = optionMatch.index + optionMatch[0].length;
      const end =
        index < optionMatches.length - 1
          ? optionMatches[index + 1].index
          : optionText.length;
      const option = optionText
        .slice(start, end)
        .replace(/,\s*(?:or\s*)?$/i, "")
        .replace(/^\s*or\s+/i, "")
        .trim();

      if (option) {
        const suggestion = normalizeNextAnalysisLabel(option);
        if (suggestion) {
          suggestions.push(suggestion);
        }
      }
    });
  }

  const lines = text.split(/\n+/);
  const startIndex = lines.findIndex((line) =>
    /recommended\s+next\s+analys|next\s+analys|наступн\w*\s+анал/i.test(line)
  );

  if (startIndex >= 0) {
    for (const line of lines.slice(startIndex + 1, startIndex + 8)) {
      const cleaned = line.replace(/^[•*-]\s*/, "").trim();
      if (!cleaned || isRecommendationsHeading(cleaned)) break;
      if (cleaned.length > 12) {
        const suggestion = normalizeNextAnalysisLabel(cleaned);
        if (suggestion) {
          suggestions.push(suggestion);
        }
      }
    }
  }

  const inlineNextStepsMatch = text.match(
    /(?:next\s+steps\s+i\s+recommend|recommended\s+next\s+analys[^:]*|next\s+analys[^:]*):(.+?)(?:limitations?:|limits?\s*&\s*notes|$)/is
  );
  if (inlineNextStepsMatch) {
    inlineNextStepsMatch[1]
      .split(/\s*[-•]\s+/)
      .map((item) => item.trim())
      .filter((item) => item.length > 12)
      .forEach((item) => {
        const suggestion = normalizeNextAnalysisLabel(item);
        if (suggestion) {
          suggestions.push(suggestion);
        }
      });
  }

  return [...new Set(suggestions)].slice(0, 3);
};

const ProductCards = ({ dataScope, marketplaceDomain, onOpenAnalytics, language }) => {
  if (dataScope?.intent === "sales_drop_causes") return null;
  if (dataScope?.intent === "ads_audit") return null;
  if (dataScope?.intent === "ad_wasted_keywords") return null;
  if (dataScope?.intent === "ad_bid_budget_changes") return null;
  if (dataScope?.intent === "ad_zero_sales_campaigns") return null;
  if (dataScope?.intent === "ad_scale_candidates") return null;
  if (dataScope?.intent === "refund_reasons") return null;
  if (dataScope?.intent === "restock_ad_plan") return null;

  const { rows, total, label } = findProductsInScope(dataScope);
  const cardLimit = Math.max(
    1,
    Math.min(40, Number(dataScope?.cardLimit) || PRODUCT_CARD_LIMIT)
  );
  const cardOffset = Math.max(0, Number(dataScope?.cardOffset) || 0);
  const productRows = rows.filter((item) => item && (item.asin || item.sku));
  const products = productRows.slice(cardOffset, cardOffset + cardLimit);

  if (!products.length) return null;

  const getProductMetrics = (product) => {
    if (dataScope?.intent === "top_products_changes") {
      const isUk = language === "uk";
      return buildTopProductChangeMetrics(product, isUk);
    }

    if (dataScope?.intent === "inventory_profitable_oos_risk") {
      const isUk = language === "uk";
      return [
        {
          label: isUk ? "Profit 30д" : "Profit 30d",
          value: product.estimated_profit_at_risk_30d,
        },
        { label: isUk ? "Unit profit" : "Unit profit", value: product.unit_profit },
        { label: isUk ? "Днів запасу" : "Days left", value: product.days_of_supply },
        { label: isUk ? "Доступно" : "Available", value: product.sellable },
      ];
    }

    if (dataScope?.intent === "inventory_inbound_delays") {
      const isUk = language === "uk";
      return [
        { label: isUk ? "У дорозі" : "In transit", value: product.shipment_in_transit_qty },
        { label: isUk ? "Днів запасу" : "Days left", value: product.days_of_supply },
        { label: isUk ? "Днів у дорозі" : "Transit days", value: product.in_transit_age_days },
        { label: isUk ? "Доступно" : "Available", value: product.sellable },
      ];
    }

    if (dataScope?.intent === "inventory_low_stock_ads") {
      const isUk = language === "uk";
      return [
        { label: isUk ? "Доступно" : "Available", value: product.sellable },
        { label: isUk ? "Днів запасу" : "Days left", value: product.days_of_supply },
        { label: "Ad spend", value: product.adspend },
        {
          label: isUk ? "Замовити" : "Order",
          value: product.recommended_order_qty,
        },
      ];
    }

    if (dataScope?.intent === "inventory_reorder_quantities") {
      const isUk = language === "uk";
      return [
        {
          label: isUk ? "Замовити" : "Order",
          value: product.recommended_order_qty,
        },
        { label: isUk ? "Доступно" : "Available", value: product.sellable },
        { label: isUk ? "Днів запасу" : "Days left", value: product.days_of_supply },
        {
          label: isUk ? "Запас 60д" : "60d stock",
          value: product.target_stock_qty,
        },
      ];
    }

    if (dataScope?.intent === "inventory_oos_products") {
      const isUk = language === "uk";
      return [
        { label: isUk ? "Доступно" : "Available", value: product.sellable },
        { label: isUk ? "Продано 30д" : "Sold 30d", value: product.sales_30days },
        {
          label: isUk ? "Ризик $" : "Risk $",
          value: product.estimated_profit_at_risk_30d,
        },
        {
          label: isUk ? "Поповн. FBA" : "FBA inbound",
          value: product.inbound_qty,
        },
      ];
    }

    if (dataScope?.intent === "inventory_risks") {
      const isUk = language === "uk";
      return [
        { label: isUk ? "Доступно" : "Available", value: product.sellable },
        { label: isUk ? "Днів запасу" : "Days left", value: product.days_of_supply },
        { label: isUk ? "Продано 30д" : "Sold 30d", value: product.sales_30days },
        {
          label: isUk ? "Поповн. FBA" : "FBA inbound",
          value: product.inbound_qty,
        },
      ];
    }

    if (dataScope?.intent === "weak_profit_ad_spend") {
      return [
        { label: "Ad spend", value: product.adspend },
        {
          label: "Profit",
          value:
            product.actual_period_profit ??
            product.estimated_period_profit ??
            product.period_profit,
        },
      ];
    }

    if ("current_sales" in product || "previous_sales" in product) {
      return [
        { label: "Now", value: product.current_sales },
        { label: "Before", value: product.previous_sales },
      ];
    }

    return [
      { label: "Sales", value: product.sales ?? product.sales_units },
      { label: "Amount", value: product.amount || product.sales_amount },
    ];
  };

  return (
    <div className="ai-product-results">
      <div className="ai-product-results__header">
        <span>{label}</span>
        {total > products.length ? (
          <small>
            {cardOffset > 0
              ? `showing ${cardOffset + 1}-${cardOffset + products.length} of ${total}`
              : `showing ${products.length} of ${total}`}
          </small>
        ) : null}
      </div>

      <div className="ai-product-grid">
        {products.map((product, index) => {
          const asin = product.asin ? String(product.asin) : "";
          const sku = product.sku ? String(product.sku) : "";
          const imageUrl = normalizeImageUrl(
            product.img || product.image || product.image_url || product.imageUrl
          );
          const title = product.title || product.name || asin || sku;
          const amazonDomain = marketplaceDomain || "amazon.com";
          const amazonUrl = asin
            ? `https://www.${amazonDomain}/dp/${encodeURIComponent(asin)}`
            : null;
          const legacyAppUrl = asin
            ? `https://app.profitpalms.com/products/dashboard?product=${encodeURIComponent(asin)}`
            : null;
          const analyticsProduct = {
            ...product,
            title,
            name: product.name || title,
            asin,
            sku,
          };
          const productMetrics = getProductMetrics(product);
          const allChangeMetrics =
            dataScope?.intent === "top_products_changes"
              ? buildTopProductChangeMetrics(product, language === "uk", {
                  includeAll: true,
                })
              : [];
          const showAllChanges = allChangeMetrics.length > productMetrics.length;
          const allChangesLabel = language === "uk" ? "Всі зміни" : "All changes";

          return (
            <article className="ai-product-card" key={`${asin}-${sku}-${index}`}>
              <div className="ai-product-card__media">
                <div className="ai-product-card__image">
                  {imageUrl ? (
                    <img src={imageUrl} alt={title} loading="lazy" />
                  ) : (
                    <i className="fa-regular fa-image"></i>
                  )}
                </div>

                <Dropdown className="ai-product-card__actions" align="start">
                  <Dropdown.Toggle
                    className="ai-product-card__actions-toggle"
                    disabled={!asin}
                    size="sm"
                    variant="outline-secondary"
                  >
                    <i className="fa-solid fa-ellipsis-vertical"></i>
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    <Dropdown.Item
                      as="button"
                      disabled={!asin}
                      onClick={() => onOpenAnalytics?.(analyticsProduct)}
                    >
                      <i className="fas fa-chart-column"></i>
                      View analytics
                    </Dropdown.Item>

                    {amazonUrl ? (
                      <Dropdown.Item href={amazonUrl} target="_blank" rel="noreferrer">
                        <i className="fa-brands fa-amazon"></i>
                        Go to Listing
                      </Dropdown.Item>
                    ) : null}

                    {legacyAppUrl ? (
                      <Dropdown.Item href={legacyAppUrl} target="_blank" rel="noreferrer">
                        <i className="fas fa-tachometer-alt"></i>
                        Go to APP Dashboard
                      </Dropdown.Item>
                    ) : null}
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              <div className="ai-product-card__body">
                <div className="ai-product-card__title" title={title}>
                  {title}
                </div>
                <dl>
                  <dt>ASIN</dt>
                  <dd>{asin || "-"}</dd>
                  <dt>SKU</dt>
                  <dd>{sku || "-"}</dd>
                </dl>

                <div className="ai-product-card__metrics">
                  {productMetrics.map((metric) => (
                    <span key={metric.label}>
                      {metric.label} {formatMetric(metric.value)}
                    </span>
                  ))}
                </div>

                {showAllChanges ? (
                  <Dropdown className="ai-product-card__changes" align="end">
                    <Dropdown.Toggle
                      className="ai-product-card__changes-toggle"
                      size="sm"
                      variant="outline-secondary"
                    >
                      {allChangesLabel}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="ai-product-card__changes-menu">
                      {allChangeMetrics.map((metric) => (
                        <div className="ai-product-card__change-row" key={metric.label}>
                          <span>{metric.label}</span>
                          <strong>{formatMetric(metric.value)}</strong>
                        </div>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

const FeedbackControls = ({ message, copy, isSending, onFeedback }) => {
  const insightId = message?.meta?.insightId;
  if (!insightId) return null;

  const selectedType = message.feedback?.feedbackType || null;
  const isSaving = message.feedback?.isSaving;
  const options = [
    {
      type: "helpful",
      icon: "fa-regular fa-thumbs-up",
      label: copy.feedbackHelpful,
    },
    {
      type: "not_helpful",
      icon: "fa-regular fa-thumbs-down",
      label: copy.feedbackNotHelpful,
    },
    {
      type: "needs_review",
      icon: "fa-regular fa-circle-question",
      label: copy.feedbackNeedsReview,
    },
  ];

  return (
    <div className="ai-feedback">
      {options.map((option) => (
        <button
          className={selectedType === option.type ? "active" : ""}
          disabled={isSending || isSaving}
          key={option.type}
          onClick={() => onFeedback(message.id, option.type)}
          type="button"
        >
          <i className={option.icon}></i>
          {option.label}
        </button>
      ))}
      {selectedType && !isSaving ? <span>{copy.feedbackSaved}</span> : null}
    </div>
  );
};

function AIAssistant() {
  const navigate = useNavigate();
  const authFetch = useMemo(() => createAuthFetch(navigate), [navigate]);
  const { account, marketplace } = useFilters();

  const [messages, setMessages] = useState(readStoredMessages);
  const [input, setInput] = useState("");
  const [conversationId, setConversationId] = useState(() =>
    localStorage.getItem(CONVERSATION_STORAGE_KEY)
  );
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [showAnalyticsPreview, setShowAnalyticsPreview] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const endRef = useRef(null);

  const accountOptions = useMemo(() => readOptions("accountOptions"), []);
  const marketplaceOptions = useMemo(() => readOptions("marketplaceOptions"), []);

  const normalizedAccount = useMemo(() => normalizeAccount(account), [account]);
  const selectedAccountLabel = useMemo(() => {
    if (account === "0" || account === 0 || account === "null" || account === null) {
      return "All accounts";
    }

    return (
      accountOptions.find((item) => String(item.value) === String(account))?.label ||
      account ||
      "No account"
    );
  }, [account, accountOptions]);

  const selectedMarketplace = useMemo(
    () => marketplaceOptions.find((item) => item.value === marketplace),
    [marketplace, marketplaceOptions]
  );
  const selectedProductTypeIds = useMemo(
    () => (Array.isArray(selectedProductType?.ids) ? selectedProductType.ids : []),
    [selectedProductType]
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const chatLanguage = useMemo(() => detectChatLanguage(messages), [messages]);
  const chatCopy = useMemo(() => getChatCopy(chatLanguage), [chatLanguage]);

  const suggestionGroups = useMemo(() => {
    const lastAssistantMessage = findLastAssistantMessage(messages);
    const scenarioMessage = findLastScenarioRootMessage(messages);
    const scenarioScope = scenarioMessage?.meta?.dataScope || null;
    const fallbackMessage = findLastResultActionSourceMessage(messages);
    const fallbackScope = fallbackMessage?.meta?.dataScope || null;
    const actionSourceMessage = scenarioMessage || fallbackMessage;
    const actionSourceScope = scenarioScope || fallbackScope;
    const currentStepScope = lastAssistantMessage?.meta?.dataScope || null;
    const showMoreMessage = findLastShowMoreSourceMessage(messages);
    const showMoreScope = showMoreMessage?.meta?.dataScope || null;
    const causeMessage = findLastScopeMessageByIntent(messages, "sales_drop_causes");
    const actionSourceIndex = actionSourceMessage ? messages.indexOf(actionSourceMessage) : -1;
    const causeIndex = causeMessage ? messages.indexOf(causeMessage) : -1;
    const causeScope = causeMessage?.meta?.dataScope || null;
    const causesAlreadyAnalyzed = !!causeScope && causeIndex > actionSourceIndex;
    const completedCauseActions = [
      hasScopeIntent(messages.slice(actionSourceIndex + 1), "ads_audit") ? CHAT_COPY.en.runAdAudit : null,
      hasScopeIntent(messages.slice(actionSourceIndex + 1), "ads_audit") ? CHAT_COPY.en.resultAdAudit : null,
      hasScopeIntent(messages.slice(actionSourceIndex + 1), "refund_reasons") ? CHAT_COPY.en.fetchRefundReasons : null,
      hasScopeIntent(messages.slice(actionSourceIndex + 1), "refund_reasons") ? CHAT_COPY.en.resultRefundReasons : null,
      hasScopeIntent(messages.slice(actionSourceIndex + 1), "restock_ad_plan") ? CHAT_COPY.en.prepareRestockPlan : null,
      hasScopeIntent(messages.slice(actionSourceIndex + 1), "inventory_risks") ? CHAT_COPY.en.resultInventoryRisks : null,
    ].filter(Boolean);
    const salesDropActionSuggestions =
      actionSourceScope?.intent === "sales_drop_analysis" && !causesAlreadyAnalyzed
        ? [
            CHAT_COPY.en.analyzeDropCauses,
            CHAT_COPY.en.resultAdAudit,
            CHAT_COPY.en.resultRefundReasons,
            CHAT_COPY.en.prepareRestockPlan,
          ].filter(
            (suggestion) => !completedCauseActions.includes(suggestion)
          )
        : [];
    const causeActionSuggestions = causesAlreadyAnalyzed
        ? buildCauseActionSuggestions(causeScope).filter(
          (suggestion) => !completedCauseActions.includes(suggestion)
        )
      : [];
    const weakProfitActionSuggestions =
      actionSourceScope?.intent === "weak_profit_ad_spend"
        ? [
            CHAT_COPY.en.resultAdAudit,
            CHAT_COPY.en.resultRefundReasons,
            CHAT_COPY.en.prepareRestockPlan,
          ].filter((suggestion) => !completedCauseActions.includes(suggestion))
        : [];
    const topProductsChangeActionSuggestions =
      actionSourceScope?.intent === "top_products_changes"
        ? [
            !hasScopeIntent(messages.slice(actionSourceIndex + 1), "ads_audit")
              ? CHAT_COPY.en.resultAdAudit
              : null,
            !hasScopeIntent(messages.slice(actionSourceIndex + 1), "refund_reasons")
              ? CHAT_COPY.en.resultRefundReasons
              : null,
            !hasScopeIntent(messages.slice(actionSourceIndex + 1), "inventory_risks")
              ? CHAT_COPY.en.resultInventoryRisks
              : null,
          ].filter(Boolean)
        : [];
    const inventoryActionSuggestions =
      actionSourceScope?.intent === "inventory_risks"
        ? [
            !hasScopeIntent(messages.slice(actionSourceIndex + 1), "inventory_oos_products")
              ? CHAT_COPY.en.showOosProducts
              : null,
            !hasScopeIntent(messages.slice(actionSourceIndex + 1), "inventory_reorder_quantities")
              ? CHAT_COPY.en.calculateReorderQty
              : null,
            !hasScopeIntent(messages.slice(actionSourceIndex + 1), "inventory_low_stock_ads")
              ? CHAT_COPY.en.showLowStockAds
              : null,
            !hasScopeIntent(messages.slice(actionSourceIndex + 1), "inventory_inbound_delays")
              ? CHAT_COPY.en.showInboundDelays
              : null,
            !hasScopeIntent(messages.slice(actionSourceIndex + 1), "inventory_profitable_oos_risk")
              ? CHAT_COPY.en.showProfitableOosRisk
              : null,
          ].filter(Boolean)
        : [];
    const currentStepActionSuggestions = AD_AUDIT_STEP_INTENTS.includes(currentStepScope?.intent)
      ? AD_AUDIT_STEP_ACTIONS.filter(
          (suggestion) =>
            !(
              (currentStepScope?.intent === "ad_wasted_keywords" &&
                suggestion === CHAT_COPY.en.findWastedAdKeywords) ||
              (currentStepScope?.intent === "ad_bid_budget_changes" &&
                suggestion === CHAT_COPY.en.suggestBidBudgetChanges) ||
              (currentStepScope?.intent === "ad_zero_sales_campaigns" &&
                suggestion === CHAT_COPY.en.showZeroSalesAdCampaigns) ||
              (currentStepScope?.intent === "ad_scale_candidates" &&
                suggestion === CHAT_COPY.en.findAdScaleCandidates)
            )
        )
      : currentStepScope?.intent === "sales_drop_causes"
      ? extractNextAnalysisSuggestions(lastAssistantMessage?.content).filter(
          (suggestion) =>
            isAdAuditRequest(suggestion) ||
            isRefundReasonsRequest(suggestion) ||
            isRestockPlanRequest(suggestion)
        )
      : causesAlreadyAnalyzed
      ? extractNextAnalysisSuggestions(lastAssistantMessage?.content).filter(
          (suggestion) =>
            isAdAuditRequest(suggestion) ||
            isRefundReasonsRequest(suggestion) ||
            isRestockPlanRequest(suggestion)
        )
      : [];
    const starterSuggestions = localizeSuggestions(CHAT_COPY.en.starterPrompts, chatLanguage);
    const productInfo = findProductsInScope(showMoreScope);
    const shownProductCount =
      (Math.max(0, Number(showMoreScope?.cardOffset) || 0) +
        Math.max(1, Number(showMoreScope?.cardLimit) || PRODUCT_CARD_LIMIT));
    const productActionSuggestions = [
      productInfo.total > shownProductCount
        ? CHAT_COPY.en.showMoreProducts
        : null,
    ].filter(Boolean);
    const resultActions = [
      ...localizeSuggestions(salesDropActionSuggestions, chatLanguage),
      ...localizeSuggestions(causeActionSuggestions, chatLanguage),
      ...localizeSuggestions(weakProfitActionSuggestions, chatLanguage),
      ...localizeSuggestions(topProductsChangeActionSuggestions, chatLanguage),
      ...localizeSuggestions(productActionSuggestions, chatLanguage),
    ]
      .filter((suggestion) => !isUnsupportedAuditAction(suggestion))
      .filter((suggestion, index, all) => all.indexOf(suggestion) === index);
    const scenarioActions = [
      ...localizeSuggestions(inventoryActionSuggestions, chatLanguage),
    ]
      .filter((suggestion) => !isUnsupportedAuditAction(suggestion))
      .filter((suggestion, index, all) => all.indexOf(suggestion) === index);
    const currentStepActionCandidates = currentStepActionSuggestions
      .filter((suggestion) => !completedCauseActions.includes(suggestion))
      .filter(
        (suggestion) =>
          AD_AUDIT_STEP_ACTIONS.includes(suggestion) || !isUnsupportedAuditAction(suggestion)
      );
    const currentStepActions = localizeSuggestions(currentStepActionCandidates, chatLanguage)
      .filter((suggestion) => !resultActions.includes(suggestion))
      .filter((suggestion) => !scenarioActions.includes(suggestion))
      .filter((suggestion, index, all) => all.indexOf(suggestion) === index);
    const resultActionGroups = [
      resultActions.length
        ? {
            label: chatCopy.resultGroup || "Actions for this result",
            items: resultActions,
          }
        : null,
      scenarioActions.length
        ? {
            label: chatCopy.scenarioGroup,
            items: scenarioActions,
          }
        : null,
      currentStepActions.length
        ? {
            label:
              AD_AUDIT_STEP_INTENTS.includes(currentStepScope?.intent)
                ? chatCopy.adAuditGroup
                : currentStepScope?.intent === "sales_drop_causes"
                ? chatCopy.salesDropCausesGroup
                : chatCopy.currentStepGroup,
            items: currentStepActions,
          }
        : null,
    ].filter(Boolean);

    return {
      resultActions: resultActionGroups.flatMap((group) => group.items),
      resultActionGroups,
      general: starterSuggestions,
    };
  }, [chatCopy, chatLanguage, messages]);

  const canSend = input.trim().length > 0 && !!marketplace && !isSending;

  useEffect(() => {
    localStorage.setItem(MESSAGES_STORAGE_KEY, JSON.stringify(messages.slice(-50)));
  }, [messages]);

  useEffect(() => {
    if (conversationId) {
      localStorage.setItem(CONVERSATION_STORAGE_KEY, conversationId);
    } else {
      localStorage.removeItem(CONVERSATION_STORAGE_KEY);
    }
  }, [conversationId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, isSending]);

  const buildPayload = useCallback(
    (content, nextMessages, extra = {}) => ({
      conversationId,
      message: content,
      account: normalizedAccount,
      marketplace,
      context: {
        accountLabel: selectedAccountLabel,
        marketplaceLabel: selectedMarketplace?.label || marketplace,
        marketplaceDomain: selectedMarketplace?.domain || null,
        productType: selectedProductType
          ? {
              title: selectedProductType.typeTitle || null,
              ids: selectedProductTypeIds,
            }
          : null,
        language: detectChatLanguage(nextMessages),
      },
      productType: selectedProductType
        ? {
            title: selectedProductType.typeTitle || null,
            ids: selectedProductTypeIds,
          }
        : null,
      productTypeIds: selectedProductTypeIds,
      history: nextMessages
        .filter((message) => message.role === "user" || message.role === "assistant")
        .slice(-HISTORY_LIMIT)
        .map((message) => ({
          role: message.role,
          content: message.content,
        })),
      ...extra,
    }),
    [
      conversationId,
      normalizedAccount,
      marketplace,
      selectedProductType,
      selectedProductTypeIds,
      selectedAccountLabel,
      selectedMarketplace,
    ]
  );

  const sendMessage = useCallback(
    async (content) => {
      const trimmed = content.trim();
      if (!trimmed || isSending || !marketplace) return;

      const userMessage = createMessage("user", trimmed);
      const nextMessages = [...messages, userMessage];
      const isShowMore = isShowMoreProductsRequest(trimmed);
      const isAnalyzeCauses = isAnalyzeCausesRequest(trimmed);
      const isWastedAdKeywords = isWastedAdKeywordsRequest(trimmed);
      const isBidBudgetChanges = isBidBudgetChangesRequest(trimmed);
      const isZeroSalesAdCampaigns = isZeroSalesAdCampaignsRequest(trimmed);
      const isAdScaleCandidates = isAdScaleCandidatesRequest(trimmed);
      const isAdAudit = isAdAuditRequest(trimmed);
      const isRefundReasons = isRefundReasonsRequest(trimmed);
      const isRestockPlan = isRestockPlanRequest(trimmed);
      const isTopProductsChanges = isTopProductsChangesRequest(trimmed);
      const isProfitableOosRisk = isProfitableOosRiskRequest(trimmed);
      const isOosProducts = isOosProductsRequest(trimmed) && !isProfitableOosRisk;
      const isReorderQty = isReorderQtyRequest(trimmed);
      const isLowStockAds = isLowStockAdsRequest(trimmed);
      const isInboundDelay = isInboundDelayRequest(trimmed);
      const isInventoryRisks = isInventoryRisksRequest(trimmed);
      const isResultInventoryRisks = isCopyActionRequest(trimmed, "resultInventoryRisks");
      const isResultAction =
        isShowMore ||
        isAnalyzeCauses ||
        isWastedAdKeywords ||
        isBidBudgetChanges ||
        isZeroSalesAdCampaigns ||
        isAdScaleCandidates ||
        isAdAudit ||
        isRefundReasons ||
        isRestockPlan ||
        isTopProductsChanges ||
        isOosProducts ||
        isReorderQty ||
        isLowStockAds ||
        isInboundDelay ||
        isProfitableOosRisk ||
        isInventoryRisks;
      const followUpMessage = isShowMore
        ? findLastShowMoreSourceMessage(messages)
        : isResultAction
        ? findLastResultActionSourceMessage(messages)
        : findLastProductMessage(messages);
      const followUpScope = followUpMessage?.meta?.dataScope || null;
      const followUpRows = getProductRowsFromScope(followUpScope);
      const followUpRange = extractScopeDateRange(followUpScope);
      const followUpCardOffset = Math.max(0, Number(followUpScope?.cardOffset) || 0);
      const followUpCardLimit = Math.max(
        1,
        Number(followUpScope?.cardLimit) || PRODUCT_CARD_LIMIT
      );
      const followUpProducts = isAnalyzeCauses || isWastedAdKeywords || isBidBudgetChanges || isZeroSalesAdCampaigns || isAdScaleCandidates || isAdAudit || isRefundReasons || isRestockPlan || isReorderQty || isResultInventoryRisks
        ? extractProductFilters(followUpRows, 50)
        : [];
      const followUpExtra =
        isTopProductsChanges
          ? {
              intent: "top_products_changes",
              limit: 50,
              cardLimit: PRODUCT_CARD_LIMIT,
            }
          : isProfitableOosRisk
          ? {
              intent: "inventory_profitable_oos_risk",
              limit: 20,
              cardLimit: PRODUCT_CARD_LIMIT,
            }
          : isOosProducts
          ? {
              intent: "inventory_oos_products",
              limit: 20,
              cardLimit: PRODUCT_CARD_LIMIT,
            }
          : isReorderQty
          ? {
              intent: "inventory_reorder_quantities",
              limit: 20,
              cardLimit: PRODUCT_CARD_LIMIT,
              ...(followUpProducts.length ? { products: followUpProducts } : {}),
            }
          : isLowStockAds
          ? {
              intent: "inventory_low_stock_ads",
              limit: 20,
              cardLimit: PRODUCT_CARD_LIMIT,
            }
          : isInboundDelay
          ? {
              intent: "inventory_inbound_delays",
              limit: 20,
              cardLimit: PRODUCT_CARD_LIMIT,
            }
          : isInventoryRisks
          ? {
              intent: "inventory_risks",
              limit: 20,
              cardLimit: PRODUCT_CARD_LIMIT,
              ...(isResultInventoryRisks && followUpProducts.length
                ? { products: followUpProducts }
                : {}),
            }
          : isWastedAdKeywords
          ? {
              intent: "ad_wasted_keywords",
              limit: 200,
              cardLimit: PRODUCT_CARD_LIMIT,
              ...(followUpProducts.length ? { products: followUpProducts } : {}),
              ...(followUpRange || {}),
            }
          : isBidBudgetChanges
          ? {
              intent: "ad_bid_budget_changes",
              limit: 300,
              cardLimit: PRODUCT_CARD_LIMIT,
              ...(followUpProducts.length ? { products: followUpProducts } : {}),
              ...(followUpRange || {}),
            }
          : isZeroSalesAdCampaigns
          ? {
              intent: "ad_zero_sales_campaigns",
              limit: 200,
              cardLimit: PRODUCT_CARD_LIMIT,
              ...(followUpProducts.length ? { products: followUpProducts } : {}),
              ...(followUpRange || {}),
            }
          : isAdScaleCandidates
          ? {
              intent: "ad_scale_candidates",
              limit: 200,
              cardLimit: PRODUCT_CARD_LIMIT,
              ...(followUpProducts.length ? { products: followUpProducts } : {}),
              ...(followUpRange || {}),
            }
          : isAdAudit
          ? {
              intent: "ads_audit",
              limit: 200,
              cardLimit: PRODUCT_CARD_LIMIT,
              ...(followUpProducts.length ? { products: followUpProducts } : {}),
              ...(followUpRange || {}),
            }
          : isRefundReasons
          ? {
              intent: "refund_reasons",
              limit: 200,
              cardLimit: PRODUCT_CARD_LIMIT,
              ...(followUpProducts.length ? { products: followUpProducts } : {}),
              ...(followUpRange || {}),
            }
          : isRestockPlan
          ? {
              intent: "restock_ad_plan",
              limit: 300,
              cardLimit: PRODUCT_CARD_LIMIT,
              ...(followUpProducts.length ? { products: followUpProducts } : {}),
              ...(followUpRange || {}),
            }
          : (isShowMore || isAnalyzeCauses) && followUpMessage
          ? {
              intent: isAnalyzeCauses ? "sales_drop_causes" : followUpScope?.intent,
              limit: isAnalyzeCauses
                ? Math.min(30, Math.max(10, followUpRows.length))
                : Math.min(60, Math.max(PRODUCT_CARD_MORE_LIMIT, followUpRows.length)),
              cardOffset: isAnalyzeCauses ? 0 : followUpCardOffset + followUpCardLimit,
              cardLimit: isAnalyzeCauses ? PRODUCT_CARD_LIMIT : PRODUCT_CARD_MORE_LIMIT,
              ...(followUpProducts.length ? { products: followUpProducts } : {}),
              ...(followUpRange || {}),
            }
          : {};

      setMessages(nextMessages);
      setInput("");
      setError(null);
      setIsSending(true);

      try {
        const response = await authFetch(ASSISTANT_ENDPOINT, {
          method: "POST",
          body: JSON.stringify(buildPayload(trimmed, nextMessages, followUpExtra)),
        });

        if (!response) return;

        if (!response.ok) {
          throw new Error(`Assistant request failed with ${response.status}`);
        }

        const result = await parseAssistantResponse(response);
        let assistantMeta = result.meta || {};
        if (isShowMore && assistantMeta.dataScope) {
          assistantMeta = {
            ...assistantMeta,
            dataScope: {
              ...assistantMeta.dataScope,
              resultAction: true,
              parentIntent: followUpScope?.intent || null,
              cardOffset:
                assistantMeta.dataScope.cardOffset ?? followUpCardOffset + followUpCardLimit,
              cardLimit: assistantMeta.dataScope.cardLimit ?? PRODUCT_CARD_MORE_LIMIT,
            },
          };
        }
        const isProductScopedResultAction =
          !isShowMore &&
          !!assistantMeta.dataScope &&
          !!followUpProducts.length &&
          (isAnalyzeCauses ||
            isWastedAdKeywords ||
            isBidBudgetChanges ||
            isZeroSalesAdCampaigns ||
            isAdScaleCandidates ||
            isAdAudit ||
            isRefundReasons ||
            isRestockPlan ||
            isReorderQty ||
            isResultInventoryRisks);
        if (isProductScopedResultAction) {
          assistantMeta = {
            ...assistantMeta,
            dataScope: {
              ...assistantMeta.dataScope,
              resultAction: true,
              parentIntent: followUpScope?.intent || null,
            },
          };
        }

        setConversationId((prev) => result.conversationId || prev);
        setMessages((prev) => [
          ...prev,
          createMessage("assistant", sanitizeAssistantReplyText(result.reply), {
            meta: assistantMeta,
          }),
        ]);
      } catch (requestError) {
        setError("Assistant backend is not available yet.");
        setMessages((prev) => [
          ...prev,
          createMessage(
            "assistant",
            "I could not reach the assistant service. The chat UI is ready; connect the backend endpoint to continue.",
            { status: "error" }
          ),
        ]);
      } finally {
        setIsSending(false);
      }
    },
    [authFetch, buildPayload, isSending, marketplace, messages]
  );

  const sendFeedback = useCallback(
    async (messageId, feedbackType) => {
      const targetMessage = messages.find((message) => message.id === messageId);
      const insightId = targetMessage?.meta?.insightId;
      if (!insightId || isSending) return;

      setMessages((prev) =>
        prev.map((message) =>
          message.id === messageId
            ? {
                ...message,
                feedback: {
                  feedbackType,
                  isSaving: true,
                },
              }
            : message
        )
      );
      setError(null);

      try {
        const response = await authFetch(ASSISTANT_FEEDBACK_ENDPOINT, {
          method: "POST",
          body: JSON.stringify({
            insightId,
            conversationId,
            feedbackType,
            reason: feedbackType,
            correction: {
              intent: targetMessage.meta?.intent || null,
            },
          }),
        });

        if (!response || !response.ok) {
          throw new Error("Feedback request failed");
        }

        setMessages((prev) =>
          prev.map((message) =>
            message.id === messageId
              ? {
                  ...message,
                  feedback: {
                    feedbackType,
                    isSaving: false,
                  },
                }
              : message
          )
        );
      } catch (feedbackError) {
        setError("Could not save assistant feedback.");
        setMessages((prev) =>
          prev.map((message) =>
            message.id === messageId
              ? {
                  ...message,
                  feedback: {
                    ...(message.feedback || {}),
                    isSaving: false,
                  },
                }
              : message
          )
        );
      }
    },
    [authFetch, conversationId, isSending, messages]
  );

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage(input);
  };

  const handleNewChat = () => {
    setMessages(initialMessages);
    setConversationId(null);
    setError(null);
  };

  const handleOpenAnalytics = useCallback((product) => {
    setSelectedProduct(product);
    setShowAnalyticsPreview(true);
  }, []);

  const handleCloseAnalytics = useCallback(() => {
    setShowAnalyticsPreview(false);
    setSelectedProduct(null);
  }, []);

  return (
    <div className="container-fluid main-container ai-assistant h-100 flex-grow-1">
      <div className="ai-assistant-shell">
        <section className="ai-chat-panel">
          <header className="ai-chat-header">
            <div>
              <h1>AI Assistant</h1>
              <div className="ai-chat-context">
                <span>{selectedAccountLabel}</span>
                <span>{selectedMarketplace?.label || marketplace || "No marketplace"}</span>
                <div className="ai-chat-type-filter">
                  <TypeTreeDropdown
                    selectedType={selectedProductType}
                    onChange={(value) => setSelectedProductType(value)}
                  />
                  {selectedProductType ? (
                    <button
                      aria-label="Clear product type filter"
                      className="ai-chat-type-filter__clear"
                      onClick={() => setSelectedProductType(null)}
                      type="button"
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            <Button
              type="button"
              variant="outline-secondary"
              size="sm"
              onClick={handleNewChat}
            >
              <i className="fa-solid fa-plus me-2"></i>
              New chat
            </Button>
          </header>

          <div className="ai-message-list" aria-live="polite">
            {messages.map((message) => {
              const displayContent =
                message.role === "assistant"
                  ? stripMarkdownImages(message.content)
                  : message.content;
              const contentLines = normalizeMessageLines(displayContent.split("\n"));

              return (
                <article
                  key={message.id}
                  className={`ai-message ${message.role} ${
                    message.status === "error" ? "error" : ""
                  }`}
                >
                  <div className="ai-message-bubble">
                    <div className="ai-message-text">
                      {contentLines.map((line, index) => (
                        <span
                          className="ai-message-line"
                          key={`${message.id}-${index}`}
                        >
                          {renderMessageLine(line, `${message.id}-${index}-content`)}
                          {index < contentLines.length - 1 ? <br /> : null}
                        </span>
                      ))}
                    </div>
                    {message.role === "assistant" ? (
                      <>
                        <ProductCards
                          dataScope={message.meta?.dataScope}
                          language={chatLanguage}
                          marketplaceDomain={selectedMarketplace?.domain}
                          onOpenAnalytics={handleOpenAnalytics}
                        />
                        <FeedbackControls
                          copy={chatCopy}
                          isSending={isSending}
                          message={message}
                          onFeedback={sendFeedback}
                        />
                      </>
                    ) : null}
                    <time>{formatTime(message.createdAt)}</time>
                  </div>
                </article>
              );
            })}

            {isSending && (
              <article className="ai-message assistant">
                <div className="ai-message-bubble pending">
                  <Spinner animation="grow" variant="secondary" size="sm" />
                  <span>Thinking...</span>
                </div>
              </article>
            )}
            <div ref={endRef} />
          </div>

          {error && <div className="ai-chat-error">{error}</div>}

          <AssistantSuggestions
            canUseActions={!!marketplace}
            generalSuggestions={suggestionGroups.general}
            isSending={isSending}
            onSelect={sendMessage}
            resultActionGroups={suggestionGroups.resultActionGroups}
            resultActionsLabel={chatCopy.resultActions}
          />

          <Form className="ai-composer" onSubmit={handleSubmit}>
            <Form.Control
              as="textarea"
              rows={1}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  if (canSend) sendMessage(input);
                }
              }}
              placeholder="Ask about products, sales, inventory, ads..."
              disabled={isSending || !marketplace}
            />
            <Button type="submit" variant="primary" disabled={!canSend}>
              <i className="fa-solid fa-paper-plane"></i>
            </Button>
          </Form>
        </section>

        <aside className="ai-assistant-side">
          <div className="ai-side-block">
            <h2>Runtime context</h2>
            <dl>
              <dt>Account</dt>
              <dd>{selectedAccountLabel}</dd>
              <dt>Marketplace</dt>
              <dd>{selectedMarketplace?.label || marketplace || "-"}</dd>
              <dt>Product type</dt>
              <dd>{selectedProductType?.typeTitle || "All types"}</dd>
              <dt>Conversation</dt>
              <dd>{conversationId || "Draft"}</dd>
            </dl>
          </div>
        </aside>
      </div>

      <Offcanvas
        show={showAnalyticsPreview}
        onHide={handleCloseAnalytics}
        placement={isMobile ? "bottom" : "end"}
        className={`product-analytics-preview ${isMobile ? "mobile" : "desktop"}`}
        style={isMobile ? {} : { width: "75vw", maxWidth: "1400px" }}
      >
        <Offcanvas.Header closeButton className="product-analytics-preview__header">
          <div className="d-flex align-items-start justify-content-between gap-3 w-100">
            <Offcanvas.Title
              className="product-analytics-preview__title"
              title={
                selectedProduct?.title ||
                selectedProduct?.name ||
                selectedProduct?.asin ||
                "Product analytics"
              }
            >
              {selectedProduct?.title ||
                selectedProduct?.name ||
                selectedProduct?.asin ||
                "Product analytics"}
            </Offcanvas.Title>

            {selectedProduct?.asin ? (
              <a
                href={`/?asin=${encodeURIComponent(selectedProduct.asin)}`}
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm btn-outline-primary flex-shrink-0 me-4"
              >
                Open full dashboard
              </a>
            ) : null}
          </div>
        </Offcanvas.Header>

        <Offcanvas.Body className="product-analytics-preview__body">
          {selectedProduct?.asin ? (
            <div className="product-analytics-preview__content">
              <AnalysisChartProducts
                account={account}
                marketplace={marketplace}
                asins={[selectedProduct.asin]}
              />
            </div>
          ) : null}
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
}

export default AIAssistant;
